import { useState, useEffect } from 'react';
import apiClient from '../../utils/api';
import { uploadInsightCoverImage } from '../../utils/submissions';

const formatInsightDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const AdminInsightsPanel = ({ addToast }) => {
  const emptyForm = () => ({
    title: "",
    excerpt: "",
    body: "",
    tag: "Platform News",
    image: "",
    published: false
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  const handleCoverUpload = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      addToast("Please choose a JPEG, PNG, or WebP image", "error");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      addToast("Image must be under 8 MB", "error");
      return;
    }
    setUploadingCover(true);
    try {
      const url = await uploadInsightCoverImage(file);
      setForm((prev) => ({ ...prev, image: url }));
      addToast("Cover image uploaded", "success");
    } catch (err) {
      addToast(err.message || "Cover upload failed", "error");
    } finally {
      setUploadingCover(false);
      e.target.value = "";
    }
  };

  const load = async (search) => {
    const q = (search !== undefined ? search : searchInput).trim();
    setLoading(true);
    try {
      const res = await apiClient.getAdminInsights(q);
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      addToast(err.message || "Could not load insights", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(""); }, []);

  const startNew = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      title: row.title || "",
      excerpt: row.excerpt || "",
      body: row.body || "",
      tag: row.tag || "Platform News",
      image: row.image || "",
      published: !!row.published
    });
  };

  const save = async (publishNow = false) => {
    setSaving(true);
    try {
      const payload = { ...form, published: publishNow ? true : form.published };
      if (editingId) {
        await apiClient.updateInsight(editingId, payload);
        if (publishNow) await apiClient.publishInsight(editingId);
        addToast(publishNow ? "Article published" : "Article saved", "success");
      } else {
        const res = await apiClient.createInsight(payload);
        const id = res.data?.id;
        if (publishNow && id) await apiClient.publishInsight(id);
        addToast(publishNow ? "Article published" : "Draft saved", "success");
      }
      startNew();
      await load();
    } catch (err) {
      addToast(err.message || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (row) => {
    try {
      if (row.published) await apiClient.unpublishInsight(row.id);
      else await apiClient.publishInsight(row.id);
      addToast(row.published ? "Unpublished" : "Published", "success");
      await load();
    } catch (err) {
      addToast(err.message || "Update failed", "error");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await apiClient.deleteInsight(row.id);
      addToast("Article deleted", "info");
      if (editingId === row.id) startNew();
      await load();
    } catch (err) {
      addToast(err.message || "Delete failed", "error");
    }
  };

  const seedDemo = async () => {
    try {
      const res = await apiClient.seedDemoInsights();
      addToast(res.data?.skipped ? "Articles already exist" : `Seeded ${res.data?.seeded || 0} demo articles`, "info");
      await load();
    } catch (err) {
      addToast(err.message || "Seed failed", "error");
    }
  };

  const tagOptions = ["Platform News", "Company", "Funding", "Market Trends", "Education", "Regulation", "Local Market", "Tax Strategy"];

  return (
    <div>
      <div className="admin-panel-head">
        <div>
          <h3><i className="fas fa-newspaper"></i> Insights &amp; company news</h3>
          <p className="admin-panel-sub">Publish updates for investors — funding rounds, product news, and market commentary. Appears on the public Insights page.</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={() => load()} disabled={loading}>
            <i className={`fas fa-sync${loading ? " fa-spin" : ""}`}></i> Refresh
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={seedDemo}>Import demo posts</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={startNew}>New article</button>
        </div>
      </div>

      <form className="insights-toolbar" onSubmit={(e) => { e.preventDefault(); load(searchInput); }}>
        <div className="insights-search">
          <i className="fas fa-search" style={{ color: "var(--text-light)" }} aria-hidden="true"></i>
          <input type="search" placeholder="Search drafts and published…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
        </div>
        <button type="submit" className="btn btn-outline btn-sm">Search</button>
      </form>

      <div className="insights-admin-form">
        <h4 style={{ marginBottom: "1rem" }}>{editingId ? "Edit article" : "New article"}</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Title</label>
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="form-group">
            <label>Category</label>
            <select value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })}>
              {tagOptions.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div className="form-group">
          <label>Excerpt (shown on card)</label>
          <textarea rows={2} required value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Full article</label>
          <textarea rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Full story text…" />
        </div>
        <div className="form-group">
          <label>Cover image</label>
          {form.image ? (
            <div style={{ marginBottom: "0.75rem", borderRadius: "var(--radius)", overflow: "hidden", maxWidth: 360, border: "1px solid var(--border)" }}>
              <img src={form.image} alt="" style={{ width: "100%", display: "block", maxHeight: 200, objectFit: "cover" }} />
            </div>
          ) : null}
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
            <label className="btn btn-outline btn-sm" style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", cursor: uploadingCover ? "wait" : "pointer" }}>
              <i className={`fas fa-${uploadingCover ? "spinner fa-spin" : "cloud-upload-alt"}`}></i>
              {uploadingCover ? "Uploading…" : form.image ? "Replace image" : "Upload image"}
              <input type="file" accept="image/jpeg,image/png,image/webp" hidden disabled={uploadingCover} onChange={handleCoverUpload} />
            </label>
            {form.image ? (
              <button type="button" className="btn btn-ghost btn-sm" disabled={uploadingCover} onClick={() => setForm((prev) => ({ ...prev, image: "" }))}>
                Remove
              </button>
            ) : null}
          </div>
          <p style={{ fontSize: "0.8rem", color: "var(--text-light)", marginTop: "0.35rem", marginBottom: 0 }}>
            JPEG, PNG, or WebP · max 8 MB. Uses the same upload path as List Property photos.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button type="button" className="btn btn-outline btn-sm" disabled={saving} onClick={() => save(false)}>
            {saving ? "Saving…" : "Save draft"}
          </button>
          <button type="button" className="btn btn-primary btn-sm" disabled={saving} onClick={() => save(true)}>
            {saving ? "Saving…" : editingId ? "Save & publish" : "Publish now"}
          </button>
          {editingId && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={startNew}>Cancel edit</button>
          )}
        </div>
      </div>

      {loading && <p style={{ color: "var(--text-light)" }}>Loading…</p>}
      {!loading && rows.length === 0 && (
        <p style={{ color: "var(--text-light)" }}>No articles yet. Create one or import demo posts.</p>
      )}
      {!loading && rows.length > 0 && (
        <div className="admin-table-wrap">
          <table className="investment-table insights-admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Status</th>
                <th>Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td><strong>{row.title}</strong><br /><span style={{ fontSize: "0.8rem", color: "var(--text-light)" }}>{row.excerpt?.slice(0, 80)}…</span></td>
                  <td>{row.tag}</td>
                  <td>{row.published ? <span className="badge badge-success">Live</span> : <span className="badge">Draft</span>}</td>
                  <td>{formatInsightDate(row.publishedAt || row.updatedAt)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => startEdit(row)}>Edit</button>{" "}
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => togglePublish(row)}>
                      {row.published ? "Unpublish" : "Publish"}
                    </button>{" "}
                    <button type="button" className="btn btn-ghost btn-sm" style={{ color: "var(--danger)" }} onClick={() => remove(row)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminInsightsPanel;
